import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon, ChevronDown } from 'lucide-react';

interface HeaderProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const Header = React.memo<HeaderProps>(({ isMenuOpen, setIsMenuOpen, isDarkMode, toggleDarkMode }) => {
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [solutionsDropdown, setSolutionsDropdown] = useState(false);
  const [academyDropdown, setAcademyDropdown] = useState(false);
  const [trainingsDropdown, setTrainingsDropdown] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  
  // Timeout refs for delayed dropdown closure
  const servicesTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const solutionsTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const academyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const trainingsTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLogoClick = () => {
    setIsFlipping(true);
    setTimeout(() => {
      setIsFlipping(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 600);
  };

  // Helper functions for delayed dropdown handling
  const handleServicesEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
    }
    setServicesDropdown(true);
  };

  const handleServicesLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setServicesDropdown(false);
    }, 300); // 300ms delay
  };

  const handleSolutionsEnter = () => {
    if (solutionsTimeoutRef.current) {
      clearTimeout(solutionsTimeoutRef.current);
    }
    setSolutionsDropdown(true);
  };

  const handleSolutionsLeave = () => {
    solutionsTimeoutRef.current = setTimeout(() => {
      setSolutionsDropdown(false);
    }, 300); // 300ms delay
  };

  const handleAcademyEnter = () => {
    if (academyTimeoutRef.current) {
      clearTimeout(academyTimeoutRef.current);
    }
    setAcademyDropdown(true);
  };

  const handleAcademyLeave = () => {
    academyTimeoutRef.current = setTimeout(() => {
      setAcademyDropdown(false);
    }, 300); // 300ms delay
  };

  const handleTrainingsEnter = () => {
    if (trainingsTimeoutRef.current) {
      clearTimeout(trainingsTimeoutRef.current);
    }
    setTrainingsDropdown(true);
  };

  const handleTrainingsLeave = () => {
    trainingsTimeoutRef.current = setTimeout(() => {
      setTrainingsDropdown(false);
    }, 300); // 300ms delay
  };

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
      if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current);
      if (academyTimeoutRef.current) clearTimeout(academyTimeoutRef.current);
      if (trainingsTimeoutRef.current) clearTimeout(trainingsTimeoutRef.current);
    };
  }, []);

  const servicesItems = [
    { name: 'Penetration Testing', href: 'penetration-testing' },
    { name: 'Vulnerability Assessment', href: 'vulnerability-assessment' },
    { name: 'Security Consulting', href: 'security-consulting' },
    { name: 'Compliance Auditing', href: 'compliance-auditing' },
    { name: 'Incident Response', href: 'incident-response' },
    { name: 'Security Training', href: 'security-training' }
  ];

  const solutionsItems = [
    { name: 'Enterprise Security', href: 'enterprise-security' },
    { name: 'Cloud Security', href: 'cloud-security' },
    { name: 'Network Security', href: 'network-security' },
    { name: 'Application Security', href: 'application-security' },
    { name: 'Data Protection', href: 'data-protection' },
    { name: 'Identity Management', href: 'identity-management' }
  ];

  const academyItems = [
    { name: 'Ethical Hacking Course', href: 'https://www.cybersoft.academy' },
    { name: 'Cybersecurity Fundamentals', href: 'https://www.cybersoft.academy' },
    { name: 'Advanced Penetration Testing', href: 'https://www.cybersoft.academy' },
    { name: 'Security Analyst Training', href: 'https://www.cybersoft.academy' },
    { name: 'Incident Response Training', href: 'https://www.cybersoft.academy' }
  ];

  const trainingsItems = [
    { name: 'Corporate Security Training', href: 'https://www.cybersoftcourses.com/' },
    { name: 'Security Awareness Programs', href: 'https://www.cybersoftcourses.com/' },
    { name: 'Technical Skills Development', href: 'https://www.cybersoftcourses.com/' },
    { name: 'Certification Preparation', href: 'https://www.cybersoftcourses.com/' },
    { name: 'Custom Training Solutions', href: 'https://www.cybersoftcourses.com/' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isDarkMode 
        ? 'bg-slate-900/95 backdrop-blur-md border-slate-800' 
        : 'bg-white/95 backdrop-blur-md border-gray-200'
    } border-b`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={handleLogoClick}
              className={`flex items-center space-x-3 transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-2 ${
                isDarkMode ? '' : 'bg-slate-900'
              } ${
                isFlipping ? 'animate-flip' : ''
              }`}
            >
              <img
                src="https://res.cloudinary.com/durgvddoz/image/upload/v1754227885/cyberlogo_f4efdi.png" 
                alt="CyberSoft Logo"
                className="h-10 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}
            >
              Home
            </Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleServicesEnter}
              onMouseLeave={handleServicesLeave}
            >
              <button className={`flex items-center space-x-1 transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}>
                <span>Services</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${servicesDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {servicesDropdown && (
                <div className={`absolute top-full left-0 mt-2 w-64 rounded-lg shadow-lg border ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-700' 
                    : 'bg-white border-gray-200'
                } py-2 z-50`}>
                  {servicesItems.map((item, index) => (
                    <Link
                      key={index}
                      to={`/services/${item.href}`}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400 hover:bg-slate-700' 
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Solutions Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleSolutionsEnter}
              onMouseLeave={handleSolutionsLeave}
            >
              <button className={`flex items-center space-x-1 transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}>
                <span>Solutions</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${solutionsDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {solutionsDropdown && (
                <div className={`absolute top-full left-0 mt-2 w-64 rounded-lg shadow-lg border ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-700' 
                    : 'bg-white border-gray-200'
                } py-2 z-50`}>
                  {solutionsItems.map((item, index) => (
                    <Link
                      key={index}
                      to={`/solutions/${item.href}`}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400 hover:bg-slate-700' 
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Academy Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleAcademyEnter}
              onMouseLeave={handleAcademyLeave}
            >
              <a
                href="https://www.cybersoft.academy"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center space-x-1 transition-colors ${
                  isDarkMode 
                    ? 'text-white hover:text-blue-400' 
                    : 'text-gray-900 hover:text-blue-600'
                }`}
              >
                <span>Academy</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${academyDropdown ? 'rotate-180' : ''}`} />
              </a>
              
              {academyDropdown && (
                <div className={`absolute top-full left-0 mt-2 w-64 rounded-lg shadow-lg border ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-700' 
                    : 'bg-white border-gray-200'
                } py-2 z-50`}>
                  {academyItems.map((item, index) => (
                    <a
                      key={index}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400 hover:bg-slate-700' 
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Trainings Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleTrainingsEnter}
              onMouseLeave={handleTrainingsLeave}
            >
              <a
                href="https://www.cybersoftcourses.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center space-x-1 transition-colors ${
                  isDarkMode 
                    ? 'text-white hover:text-blue-400' 
                    : 'text-gray-900 hover:text-blue-600'
                }`}
              >
                <span>Trainings</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${trainingsDropdown ? 'rotate-180' : ''}`} />
              </a>
              
              {trainingsDropdown && (
                <div className={`absolute top-full left-0 mt-2 w-64 rounded-lg shadow-lg border ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-700' 
                    : 'bg-white border-gray-200'
                } py-2 z-50`}>
                  {trainingsItems.map((item, index) => (
                    <a
                      key={index}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400 hover:bg-slate-700' 
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/#about" 
              className={`transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}
             onClick={() => {
               if (window.location.pathname === '/') {
                 setTimeout(() => {
                   document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                 }, 100);
               }
             }}
            >
              About
            </Link>
            <Link 
             to="/#contact"
              className={`transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}
             onClick={() => {
               if (window.location.pathname === '/') {
                 setTimeout(() => {
                   document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                 }, 100);
               }
             }}
            >
              Contact
            </Link>
          </nav>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'text-white hover:bg-slate-800' 
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'text-white hover:bg-slate-800' 
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <button
              onClick={toggleMenu}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'text-white hover:bg-slate-800' 
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className={`md:hidden border-t ${
            isDarkMode ? 'border-slate-800' : 'border-gray-200'
          } py-4 space-y-4`}>
            <Link 
              to="/" 
              className={`block transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}
            >
              Home
            </Link>

            {/* Mobile Services */}
            <div>
              <button 
                onClick={() => setServicesDropdown(!servicesDropdown)}
                className={`flex items-center justify-between w-full transition-colors py-2 ${
                  isDarkMode 
                    ? 'text-white hover:text-blue-400' 
                    : 'text-gray-900 hover:text-blue-600'
                }`}
              >
                Services
                <ChevronDown className={`h-4 w-4 transition-transform ${servicesDropdown ? 'rotate-180' : ''}`} />
              </button>
              {servicesDropdown && (
                <div className="pl-4 space-y-1">
                  {servicesItems.map((item, index) => (
                    <Link
                      key={index}
                      to={`/services/${item.href}`}
                      className={`block py-1 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400' 
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Solutions */}
            <div>
              <button 
                onClick={() => setSolutionsDropdown(!solutionsDropdown)}
                className={`flex items-center justify-between w-full transition-colors py-2 ${
                  isDarkMode 
                    ? 'text-white hover:text-blue-400' 
                    : 'text-gray-900 hover:text-blue-600'
                }`}
              >
                Solutions
                <ChevronDown className={`h-4 w-4 transition-transform ${solutionsDropdown ? 'rotate-180' : ''}`} />
              </button>
              {solutionsDropdown && (
                <div className="pl-4 space-y-1">
                  {solutionsItems.map((item, index) => (
                    <Link
                      key={index}
                      to={`/solutions/${item.href}`}
                      className={`block py-1 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400' 
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Academy */}
            <div>
              <button 
                onClick={() => setAcademyDropdown(!academyDropdown)}
                className={`flex items-center justify-between w-full transition-colors py-2 ${
                  isDarkMode 
                    ? 'text-white hover:text-blue-400' 
                    : 'text-gray-900 hover:text-blue-600'
                }`}
              >
                <a 
                  href="https://www.cybersoft.academy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Academy
                </a>
                <ChevronDown className={`h-4 w-4 transition-transform ${academyDropdown ? 'rotate-180' : ''}`} />
              </button>
              {academyDropdown && (
                <div className="pl-4 space-y-1">
                  {academyItems.map((item, index) => (
                    <a
                      key={index}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block py-1 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400' 
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Trainings */}
            <div>
              <button 
                onClick={() => setTrainingsDropdown(!trainingsDropdown)}
                className={`flex items-center justify-between w-full transition-colors py-2 ${
                  isDarkMode 
                    ? 'text-white hover:text-blue-400' 
                    : 'text-gray-900 hover:text-blue-600'
                }`}
              >
                <a 
                  href="https://www.cybersoftcourses.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Trainings
                </a>
                <ChevronDown className={`h-4 w-4 transition-transform ${trainingsDropdown ? 'rotate-180' : ''}`} />
              </button>
              {trainingsDropdown && (
                <div className="pl-4 space-y-1">
                  {trainingsItems.map((item, index) => (
                    <a
                      key={index}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block py-1 text-sm transition-colors ${
                        isDarkMode 
                          ? 'text-slate-300 hover:text-blue-400' 
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link 
             to="/#contact"
              className={`block transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}
             onClick={() => {
               if (window.location.pathname === '/') {
                 setTimeout(() => {
                   document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                 }, 100);
               }
             }}
            >
              About
            </Link>
            <Link 
              to="/#contact" 
              className={`block transition-colors ${
                isDarkMode 
                  ? 'text-white hover:text-blue-400' 
                  : 'text-gray-900 hover:text-blue-600'
              }`}
             onClick={() => {
               if (window.location.pathname === '/') {
                 setTimeout(() => {
                   document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                 }, 100);
               }
             }}
            >
              Contact
            </Link>
          </div>
        )}
      </div>
    </header>
  );
});

export default Header;